import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, X, Play, ImagePlus, Video } from "lucide-react";
import { uploadMedia, shrinkImage, checkVideo } from "../lib/upload";

const MAX_IMAGES = 10;
const errText = (err) => err.response?.data?.error || err.response?.data?.detail || "Upload mislykkedes – prøv igen.";

function MediaTile({ m, i, n, onMove, onRemove }) {
  return (
    <div className="mp-tile" data-testid={`media-item-${i}`}>
      <img src={m.thumb_url} alt="" />
      {m.kind === "video" && <span className="mp-play"><Play size={14} fill="currentColor" /> {m.duration ? `${Math.round(m.duration)} s` : "Video"}</span>}
      {i === 0 && m.kind === "image" && <span className="mp-cover">Forside</span>}
      <div className="mp-tools">
        {m.kind === "image" && i > 0 && <button type="button" aria-label="Flyt til venstre" onClick={() => onMove(i, -1)} data-testid={`media-left-${i}`}><ArrowLeft size={14} /></button>}
        {m.kind === "image" && i < n - 1 && <button type="button" aria-label="Flyt til højre" onClick={() => onMove(i, 1)} data-testid={`media-right-${i}`}><ArrowRight size={14} /></button>}
        <button type="button" aria-label="Fjern" onClick={() => onRemove(i)} data-testid={`media-remove-${i}`}><X size={14} /></button>
      </div>
    </div>
  );
}

export default function MediaPicker({ media, onChange, legacyImage, disabled }) {
  const [mode, setMode] = useState(media[0]?.kind === "video" ? "video" : "image");
  const [prog, setProg] = useState(null);
  const [err, setErr] = useState("");
  const ref = useRef(null);
  const busy = prog !== null;
  const firstKind = media[0]?.kind;
  useEffect(() => { if (firstKind) setMode(firstKind); }, [firstKind]);

  const switchMode = (m) => {
    if (m === mode || busy) return;
    if (media.length && !window.confirm(m === "video" ? "Skift til video? De valgte billeder fjernes fra tilbuddet." : "Skift til billeder? Videoen fjernes fra tilbuddet.")) return;
    if (media.length) onChange([]);
    setMode(m);
    setErr("");
  };
  const pick = async (e) => {
    const files = [...e.target.files];
    e.target.value = "";
    if (!files.length) return;
    setErr("");
    try {
      if (mode === "video") {
        const bad = await checkVideo(files[0]);
        if (bad) return setErr(bad);
        setProg(0);
        const m = await uploadMedia(files[0], "video", files[0].name, setProg);
        onChange([m]);
      } else {
        const room = MAX_IMAGES - media.length;
        if (files.length > room) setErr(`Højst ${MAX_IMAGES} billeder – de første ${room} er valgt.`);
        let next = [...media];
        for (const [k, f] of files.slice(0, room).entries()) {
          setProg(0);
          const blob = await shrinkImage(f);
          const m = await uploadMedia(blob, "image", f.name, (p) => setProg((k + p) / Math.min(files.length, room)));
          next = [...next, m];
          onChange(next);
        }
      }
    } catch (ex) {
      setErr(errText(ex));
    } finally {
      setProg(null);
    }
  };
  const move = (i, d) => { const a = [...media]; [a[i], a[i + d]] = [a[i + d], a[i]]; onChange(a); };
  const remove = (i) => onChange(media.filter((_, k) => k !== i));
  const full = mode === "video" ? media.length >= 1 : media.length >= MAX_IMAGES;

  return (
    <div className="mp" data-testid="media-picker">
      <div className="mp-modes" role="tablist">
        <button type="button" role="tab" aria-selected={mode === "image"} onClick={() => switchMode("image")} data-testid="media-mode-images"><ImagePlus size={15} /> Billeder</button>
        <button type="button" role="tab" aria-selected={mode === "video"} onClick={() => switchMode("video")} data-testid="media-mode-video"><Video size={15} /> Video</button>
      </div>
      {!media.length && legacyImage && <img className="mp-legacy" src={legacyImage} alt="" />}
      {media.length > 0 && (
        <div className="mp-grid">{media.map((m, i) => <MediaTile key={m.id} m={m} i={i} n={media.length} onMove={move} onRemove={remove} />)}</div>
      )}
      {busy && (
        <div className="mp-progress" data-testid="media-progress"><span style={{ width: `${Math.round(prog * 100)}%` }} /><small>Uploader… {Math.round(prog * 100)} %</small></div>
      )}
      {err && <p className="mp-error" role="alert" data-testid="media-error">{err}</p>}
      {!full && (
        <label className={`btn${busy || disabled ? " disabled" : ""}`} style={{ width: "100%" }} data-testid="media-add">
          {mode === "video" ? "Vælg video" : media.length ? "Tilføj flere billeder" : "Vælg billeder"}
          <input ref={ref} type="file" className="visually-hidden" disabled={busy || disabled} multiple={mode === "image"}
            accept={mode === "video" ? "video/mp4,video/quicktime,.mp4,.mov,.m4v" : "image/png,image/jpeg,image/webp,image/heic"} onChange={pick} data-testid="media-file-input" />
        </label>
      )}
      <p className="hint muted">
        {mode === "video" ? "Én video, højst 1 minut (MP4/MOV, fx fra iPhone). Kunden ser et stillbillede fra videoen." : `Op til ${MAX_IMAGES} billeder – det første er forsiden. Billederne gøres mindre automatisk.`}
      </p>
    </div>
  );
}
